import { Accordion } from '@base-ui/react';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, Button } from '~/components';
import { Checkbox, Field, Input, NumberInput } from '~/components/Form';

import type { EditEntryNumberFormProps as Props } from './dialogs.types';

import {
  entryNumberLabelStyle,
  entryNumberValueStyle,
  expandedFormStyle,
  fieldsRowStyle,
  panelStyle,
  prefixFieldStyle,
  serialNumberFieldStyle,
  summaryActionsStyle,
  summaryRowStyle,
  wrapperStyle,
} from './EditEntryNumberForm.css';

export const EditEntryNumberForm = ({ fcEntryNumber, form, numberingSeries, type }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    control,
    formState: { defaultValues, isDirty },
    reset,
    setValue,
    watch,
  } = form;

  const showSuffix = watch('showSuffix');

  const { t } = useTranslation(['invoices', 'settings']);

  useEffect(() => {
    if (!showSuffix) setValue('suffix', '');
  }, [showSuffix, setValue]);

  return (
    <Accordion.Root className={wrapperStyle}>
      <Accordion.Item onOpenChange={setIsOpen} value="entryNumber">
        <div className={summaryRowStyle}>
          <p className={entryNumberLabelStyle}>
            <strong>
              {type === 'invoice'
                ? t('invoices:createEdit.confirmation.entryNumber.invoiceNumber')
                : t('invoices:createEdit.confirmation.entryNumber.creditNoteNumber')}
            </strong>

            <span className={entryNumberValueStyle}>{fcEntryNumber}</span>
          </p>

          <div className={summaryActionsStyle}>
            {isOpen && isDirty && (
              <Button
                aria-label={t('invoices:createEdit.confirmation.entryNumber.reset')}
                icon="Undo"
                onClick={() => reset(defaultValues)}
              />
            )}

            <Accordion.Header>
              <Accordion.Trigger
                render={
                  <Button
                    aria-label={
                      !isOpen
                        ? t('invoices:createEdit.confirmation.entryNumber.edit')
                        : isDirty
                          ? t('invoices:createEdit.confirmation.entryNumber.confirm')
                          : t('invoices:createEdit.confirmation.entryNumber.close')
                    }
                    icon={!isOpen ? 'Edit' : isDirty ? 'Check' : 'CloseSmall'}
                  />
                }
              />
            </Accordion.Header>
          </div>
        </div>

        <Accordion.Panel className={panelStyle}>
          <form className={expandedFormStyle}>
            <Alert announce={false} icon="Info" status="warning">
              <p>{t('invoices:createEdit.confirmation.entryNumber.warning')}</p>

              {fcEntryNumber.length > 30 && <p>{t('settings:fields.numberingSeries.format.lengthWarning', { limit: 30 })}</p>}
            </Alert>

            <div className={fieldsRowStyle}>
              {/* Prefix */}
              <Controller
                control={control}
                name="prefix"
                render={({ field: { name, onBlur, onChange, ref, value } }) => (
                  <Field.Root className={prefixFieldStyle} name={name}>
                    <Field.Label>{t('invoices:createEdit.confirmation.entryNumber.prefix')}</Field.Label>
                    <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />
                  </Field.Root>
                )}
              />

              {/* Serial number */}
              <Controller
                control={control}
                name="nextNumber"
                render={({ field: { name, onBlur, onChange, ref, value } }) => (
                  <Field.Root className={serialNumberFieldStyle} name={name}>
                    <Field.Label>{t('invoices:createEdit.confirmation.entryNumber.serialNumber')}</Field.Label>

                    <NumberInput
                      format={{
                        minimumIntegerDigits: numberingSeries.format.amountOfDigits ?? 1,
                        maximumFractionDigits: 0,
                        useGrouping: false,
                      }}
                      max={10 ** (numberingSeries.format.amountOfDigits ?? 4) - 1}
                      min={defaultValues?.nextNumber ?? 0}
                      onBlur={onBlur}
                      onValueChange={(nextNumber) => onChange(nextNumber ?? defaultValues?.nextNumber ?? 0)}
                      ref={ref}
                      showSteppers
                      smallStep={1}
                      step={1}
                      value={value}
                    />
                  </Field.Root>
                )}
              />
            </div>

            {/* Show suffix */}
            <Controller
              control={control}
              name="showSuffix"
              render={({ field: { name, onChange, value } }) => (
                <Field.Root name={name}>
                  <Checkbox.Root checked={value} name={name} onCheckedChange={onChange}>
                    <Checkbox.Label>{t('settings:fields.numberingSeries.format.useSuffix')}</Checkbox.Label>
                  </Checkbox.Root>
                </Field.Root>
              )}
            />

            {/* Suffix */}
            {showSuffix && (
              <Controller
                control={control}
                name="suffix"
                render={({ field: { name, onBlur, onChange, ref, value } }) => (
                  <Field.Root name={name}>
                    <Field.Label>{t('invoices:createEdit.confirmation.entryNumber.suffix')}</Field.Label>
                    <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />
                  </Field.Root>
                )}
              />
            )}
          </form>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
};
